import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, getArticlesByCategory, getCategoryBySlug, type Article } from '@/mocks/articleData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const [activeSlug, setActiveSlug] = useState(slug || 'features');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
    window.scrollTo({ top: 0, behavior: 'instant' } as ScrollToOptions);
  }, [slug]);

  const activeCategory = getCategoryBySlug(activeSlug);
  const currentArticles = getArticlesByCategory(activeSlug);

  const categorySchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${activeCategory?.label || ''} - hlw 平台`,
    description: activeCategory?.description || '',
    url: `https://www.hlw.com/category/${activeSlug}`,
    inLanguage: "zh-CN",
    isPartOf: {
      "@type": "WebSite",
      name: "hlw 平台",
      url: "https://www.hlw.com",
    },
  };

  const categoryLabels: Record<string, string> = {
    features: '功能介绍',
    guide: '使用指南',
    scenarios: '适用场景',
    reviews: '用户评价',
  };

  return (
    <>
      <SEOHead
        title={`${activeCategory?.label || ''} - hlw 平台`}
        description={activeCategory?.description || 'hlw 平台分类页面'}
        keywords={`hlw,hlw平台,${activeCategory?.label || ''},hlw${categoryLabels[activeSlug] || ''}`}
        canonicalUrl={`/category/${activeSlug}`}
        schemaJson={categorySchema}
      />
      <Navbar />
      <main className="min-h-screen bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-16">
          <Breadcrumb
            items={[
              { label: t('breadcrumb.home'), href: '/' },
              { label: activeCategory?.label || '' },
            ]}
          />

          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2 mt-4">
            {activeCategory?.label}
          </h1>
          <p className="text-foreground-600 mb-8">{activeCategory?.description}</p>

          <div className="flex items-center gap-2 mb-10 overflow-x-auto flex-wrap">
            {categories.map((cat) => (
              <a
                key={cat.slug}
                href={`/category/${cat.slug}`}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                    : 'bg-background-100 text-foreground-600 hover:text-primary-500 hover:bg-primary-50/70 border border-background-200/60'
                }`}
              >
                {cat.label}
              </a>
            ))}
          </div>

          {currentArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {currentArticles.map((article: Article) => (
                <article
                  key={article.id}
                  className="rounded-lg bg-background-100 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 group"
                >
                  <a href={`/article/${article.id}`} className="block">
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-5">
                      <h3 className="text-base md:text-lg font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed mb-3 line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-foreground-500">
                        <span>{article.date}</span>
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line w-3 h-3 flex items-center justify-center"></i>
                          {article.readTime}
                        </span>
                      </div>
                    </div>
                  </a>
                </article>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">{t('category.noArticles')}</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}